import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

export interface PasswordValidation {
  minLength: boolean;
  hasUpper: boolean;
  hasLower: boolean;
  hasNumber: boolean;
  isValid: boolean;
}

interface PasswordValidatorProps {
  password: string;
  showValidation: boolean;
  validation?: PasswordValidation;
}

export const validatePassword = (password: string): PasswordValidation => {
  // Match exact backend validation requirements from register.dto.ts
  const minLength = password.length >= 8;
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  
  return {
    minLength,
    hasUpper,
    hasLower,
    hasNumber,
    isValid: minLength && hasUpper && hasLower && hasNumber
  };
};

const ValidationItem: React.FC<{ 
  label: string; 
  isValid: boolean; 
  hasContent: boolean 
}> = ({ label, isValid, hasContent }) => {
  const getItemStyle = () => {
    if (!hasContent) {
      return 'text-gray-500';
    }
    return isValid ? 'text-green-600' : 'text-red-600';
  };

  const getIcon = () => {
    if (!hasContent) {
      return <div className="w-4 h-4 rounded-full border border-gray-300" />;
    }
    return isValid ? (
      <CheckIcon className="w-4 h-4 text-green-600" />
    ) : (
      <XMarkIcon className="w-4 h-4 text-red-600" />
    );
  };

  return (
    <div className={`flex items-center space-x-2 text-sm ${getItemStyle()}`}>
      {getIcon()}
      <span>{label}</span>
    </div>
  );
};

export const PasswordValidator: React.FC<PasswordValidatorProps> = ({
  password,
  showValidation,
  validation: providedValidation
}) => {
  const { t } = useTranslation('auth');
  const validation = providedValidation || validatePassword(password);
  const hasContent = password.length > 0;

  if (!showValidation && !hasContent) {
    return null;
  }

  return (
    <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-md space-y-2">
      
      <ValidationItem 
        label={t('password.minLength')} 
        isValid={validation.minLength} 
        hasContent={hasContent}
      />
      <ValidationItem 
        label={t('password.hasUpper')} 
        isValid={validation.hasUpper} 
        hasContent={hasContent}
      />
      <ValidationItem 
        label={t('password.hasLower')} 
        isValid={validation.hasLower} 
        hasContent={hasContent}
      />
      <ValidationItem 
        label={t('password.hasNumber')} 
        isValid={validation.hasNumber} 
        hasContent={hasContent}
      />
    </div>
  );
};